var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { CustomEvent } from '@shared/utils/CustomEvents';
import { DataCache } from '../DataCache';
import { TvSymbolConvertor } from './Convertors/TvSymbolConvertor';
import { TvHistoryManager } from './Managers/TvHistoryManager';
import { TvSearchSymbolManager } from './Managers/TvSearchSymbolManager';
import { TvQuoteManager } from './Managers/TvQuoteManager';
import { TvInteriorIdCache } from './Caches/TvInteriorIdCache';
import { TvDomManager } from './Managers/TvDomManager';
import { TvExchangesManger } from './Managers/TvExchangesManger';
import { TvInstrumentTypesManager } from './Managers/TvInstrumentTypesManager';
import { TvHistoryAggregationsHelper } from './Helpers/TvHistoryAggregationsHelper';
export class TvDataFeed {
    constructor() {
        this.onReadyEvent = new CustomEvent();
        this.historyManager = new TvHistoryManager();
        this.quoteManager = new TvQuoteManager();
        this.searchSymbolManager = new TvSearchSymbolManager();
        this.domManager = new TvDomManager();
        // #endregion IDestroyable
    }
    // #region TradingView DataFeed interface implementation
    onReady(callback /*: OnReadyCallback */) {
        setTimeout(() => {
            const symbolGrouping = {
                futures: new RegExp('^\\[?(/[A-Z0-9_]+)[A-Z]\\d{2}\\]?$') // TODO: ASK SUPPORT
            };
            const configuration = 
            // : DatafeedConfiguration =
            {
                supported_resolutions: TvHistoryAggregationsHelper.getSupportedResolutions(),
                exchanges: TvExchangesManger.getExchanges(),
                symbols_types: TvInstrumentTypesManager.getInstrumentTypes(),
                symbols_grouping: symbolGrouping
            };
            callback(configuration);
            this.onReadyEvent.Raise();
        }, 0);
    }
    searchSymbols(userInput, exchange, symbolType, onResultReadyCallback) {
        return __awaiter(this, void 0, void 0, function* () {
            const filteredList = yield this.searchSymbolManager.searchSymbols(userInput, exchange, symbolType);
            const result = [];
            for (const instrument of filteredList) {
                const resultItem = TvSymbolConvertor.convertToTvSearchSymbolResultItem(instrument);
                result.push(resultItem);
            }
            onResultReadyCallback(result);
        });
    }
    resolveSymbol(symbolName, onResolve, onError, extension) {
        try {
            setTimeout(() => {
                if (TvSymbolConvertor.isFakeSymbolName(symbolName)) // кейс, когда резолвится скрытый инпут индикатора
                 {
                    const fakeInfo = TvSymbolConvertor.getFakeSymbol();
                    onResolve(fakeInfo);
                    return;
                }
                const interiorId = TvInteriorIdCache.getInteriorId(symbolName);
                const instrument = DataCache.getInstrumentByName(interiorId);
                if (!instrument) {
                    onError(`[TvDataFeed::resolveSymbol]: Instrument ${symbolName} not found`);
                    return;
                }
                const symbolInfo = TvSymbolConvertor.convertToTvSymbol(instrument, extension);
                onResolve(symbolInfo);
            }, 0); // Datafeed.resolveSymbol must be async: https://www.tradingview.com/charting-library-docs/latest/connecting_data/Datafeed-API/#asynchronous-callbacks
        }
        catch (err) {
            onError(err.message);
        }
    }
    getBars(symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.historyManager.getBars(symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback);
        });
    }
    subscribeBars(symbolInfo, resolution, onRealtimeCallback, subscriberUID, onResetCacheNeededCallback) {
        this.historyManager.subscribeBars(symbolInfo, resolution, onRealtimeCallback, subscriberUID, onResetCacheNeededCallback);
    }
    unsubscribeBars(subscriberUID) {
        this.historyManager.unsubscribeBars(subscriberUID);
    }
    // #endregion TradingView DataFeed interface implementation
    setCurrentAccount(account) {
        this.historyManager.setCurrentAccount(account);
        this.quoteManager.setCurrentAccount(account);
        this.domManager.setCurrentAccount(account);
    }
    getSymbolByName(nameWithExchange) {
        return this.searchSymbolManager.getSymbolByName(nameWithExchange);
    }
    // #region IDatafeedQuotesApi - works only for trading platform
    getQuotes(symbols, onDataCallback, onErrorCallback) {
        this.quoteManager.getQuotes(symbols, onDataCallback, onErrorCallback);
    }
    subscribeQuotes(symbols, fastSymbols, onRealtimeCallback, listenerGUID) {
        this.quoteManager.subscribeQuotes(symbols, fastSymbols, onRealtimeCallback, listenerGUID);
    }
    unsubscribeQuotes(listenerGUID) {
        this.quoteManager.unsubscribeQuotes(listenerGUID);
    }
    // #endregion
    // #region  Level2
    subscribeDepth(symbol, callback) {
        return this.domManager.subscribeDepth(symbol, callback);
    }
    unsubscribeDepth(subscriberUID) {
        this.domManager.unsubscribeDepth(subscriberUID);
    }
    // #endregion
    // #region IDestroyable
    destroy() {
        this.searchSymbolManager.dispose();
        this.historyManager.dispose();
    }
}
